import { ref, computed } from 'vue';
import { billingService, type Subscription, type PlanFeatures } from '../services/billing';

// Module-level state (shared across all components using this composable)
const subscription = ref<Subscription | null>(null);
const features = ref<Partial<PlanFeatures> | null>(null);
const loading = ref(true);
const loaded = ref(false);

export function useEntitlements() {
  const planType = computed(() => subscription.value?.plan_type ?? 'free');

  /**
   * Load subscription and features. Called once on app mount.
   * Fail-open: if billing service is unreachable, features stays null
   * and all hasFeature() checks return true.
   */
  async function load(): Promise<void> {
    if (loaded.value) return; // Already loaded this session
    loading.value = true;
    try {
      const sub = await billingService.getCurrentSubscription();
      subscription.value = sub;
      features.value = sub?.features ?? null;
    } catch {
      // Fail open — billing outage should never lock users out of the UI
      subscription.value = null;
      features.value = null;
    } finally {
      loading.value = false;
      loaded.value = true;
    }
  }

  /**
   * Check if a specific feature is enabled.
   * @param featurePath - dot-notation path, e.g. "channels.voice" or "ai.rag_knowledge_base"
   * @returns true if feature is enabled, or true if features not loaded (fail open)
   */
  function hasFeature(featurePath: string): boolean {
    if (!features.value) return true; // fail open

    const [category, feature] = featurePath.split('.');
    if (!category || !feature) return true;

    const categoryFeatures = (features.value as Record<string, Record<string, boolean>>)[category];
    if (!categoryFeatures) return true; // unknown category — fail open

    return categoryFeatures[feature] ?? false;
  }

  /**
   * Check if ANY of the given features are enabled.
   * Used for group-level gating where a group requires at least one feature.
   */
  function hasAnyFeature(featurePaths: string[]): boolean {
    if (!features.value) return true; // fail open
    return featurePaths.some(f => hasFeature(f));
  }

  /**
   * Check if ANY feature in a category is enabled.
   * Used for wildcard checks like "integrations.*" or "channels.*".
   * @param category - category name, e.g. "integrations" or "channels"
   * @param exclude - optional feature keys to exclude from the check
   */
  function hasAnyInCategory(category: string, exclude: string[] = []): boolean {
    if (!features.value) return true; // fail open

    const categoryFeatures = (features.value as Record<string, Record<string, boolean>>)[category];
    if (!categoryFeatures) return true;

    return Object.entries(categoryFeatures).some(
      ([key, value]) => !exclude.includes(key) && value === true
    );
  }

  /**
   * Reset state (useful for logout)
   */
  function reset(): void {
    subscription.value = null;
    features.value = null;
    loading.value = true;
    loaded.value = false;
  }

  return {
    subscription,
    features,
    loading,
    loaded,
    planType,
    load,
    hasFeature,
    hasAnyFeature,
    hasAnyInCategory,
    reset,
  };
}
